<template>
    <div>
        <template v-if="loading">
            Loading <i class="fa fa-spinner fa-spin"></i>
        </template>
        <template v-else>
            <vue-headful :title="'[View Product] ' + product.product_name + ' - AMOS365'" />

            <nav aria-label="breadcrumb">
                <ol class="breadcrumb">
                    <li class="breadcrumb-item">
                        <router-link to="/products">Products</router-link>
                    </li>
                    <li class="breadcrumb-item active" aria-current="page">{{ product.product_name }}</li>
                </ol>
            </nav>

            <h4 class="page-header mb-4">
                {{ product.product_name }} <span class="badge badge-warning" v-if="product.is_active === 0">Inactive</span>
            </h4>

            <div class="mb-3" v-if="canEdit">
                <router-link :to="`/products/edit/${product.id}`" class="btn btn-sm btn-default"><i class="fas fa-edit"></i> Edit</router-link>
                <router-link :to="`/products/sync-levels/${product.id}`" class="btn btn-sm btn-default"><i class="fas fa-sync"></i> Sync Levels</router-link>
                <router-link :to="`/products/deactivate/${product.id}`" class="btn btn-sm btn-warning" v-if="product.is_active == 1"><i class="fas fa-power-off"></i> Deactivate</router-link>

                <template v-if="$store.state.user.is_developer === 1">
                    <router-link :to="`/products/delete/${product.id}`" class="btn btn-sm btn-default"><span class="text-danger"><i class="fas fa-trash"></i> Delete</span></router-link>
                </template>
            </div>

            <div class="row mb-3">
                <div class="col-md-8 col-12">
                    <div class="card mb-4">
                        <div class="card-header">
                            <strong>Details:</strong>
                        </div>
                        <div class="card-body">
                            <div class="row mb-3">
                                <div class="col-lg-3 col-md-4 col-12 text-md-right">Product:</div>
                                <div class="col-md-8 col-12 input-group-sm">
                                    {{ product.product_name }}
                                </div>
                            </div>
                            <div class="row mb-3">
                                <div class="col-lg-3 col-md-4 col-12 text-md-right">Product Type:</div>
                                <div class="col-md-8 col-12 input-group-sm">
                                    {{ product.product_type_name }}
                                </div>
                            </div>
                            <div class="row mb-3">
                                <div class="col-lg-3 col-md-4 col-12 text-md-right">Carrier:</div>
                                <div class="col-md-8 col-12 input-group-sm">
                                    <router-link :to="`/carriers/view/${product.carrier_id}`" v-if="product.carrier_id">
                                        {{ product.carrier_name }}
                                    </router-link>
                                </div>
                            </div>
                            <div class="row mb-3">
                                <div class="col-lg-3 col-md-4 col-12 text-md-right">Website:</div>
                                <div class="col-md-8 col-12 input-group-sm">
                                    {{ product.product_website }}
                                </div>
                            </div>
                            <div class="row mb-3">
                                <div class="col-lg-3 col-md-4 col-12 text-md-right">Expiration:</div>
                                <div class="col-md-8 col-12 input-group-sm">
                                    <span v-if="product.product_expires_at">{{ product.product_expires_at | moment("MM/DD/YYYY") }}</span>
                                </div>
                            </div>
                            <div class="row mb-3">
                                <div class="col-lg-3 col-md-4 col-12 text-md-right">Active:</div>
                                <div class="col-md-8 col-12 input-group-sm">
                                    <label class="badge badge-success" v-if="product.is_active == 1">Active</label>
                                    <label class="badge badge-warning" v-else-if="product.is_active == 0">Inactive</label>
                                </div>
                            </div>
                            <div class="row mb-3">
                                <div class="col-lg-3 col-md-4 col-12 text-md-right">Description:</div>
                                <div class="col-md-8 col-12 input-group-sm">
                                    {{ product.product_desc }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-md-4 col-12 mt-md-0 mt-4">
                    <div class="card mb-4">
                        <div class="card-header">
                            <strong>Levels ({{ product.levels.length }}):</strong>
                        </div>
                        <div class="card-body" style="max-height: 260px; overflow-y: auto;">
                            <ol style="padding-left: 10px;">
                                <li v-for="level in product.levels" class="mb-3">
                                    {{ level.product_level_name }} <span v-if="level.commission" title="Product Level Commission">- {{ level.commission }}</span>

                                    <span class="text-muted d-block mt-1" v-if="level.description">{{ level.description }}</span>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <ul class="search-nav nav nav-tabs tabs-alt container-m--x mt-4">
                <b-nav-item :active="curTab === 'sold_by'" @click="curTab = 'sold_by'">
                    Sold By ({{ agentCount }})
                </b-nav-item>
                <b-nav-item :active="curTab === 'history'" @click="curTab = 'history'">
                    <i class="fas fa-clock"></i> History <span v-if="historyCount">({{ historyCount }})</span>
                </b-nav-item>
            </ul>

            <div class="pt-2">
                <div class="card" v-show="curTab === 'sold_by'">
                    <div class="card-body">
                        <v-server-table ref="productContractTable" :url="`/products/contracts/${product.id}`" :columns="columns" :options="options">

                            <div slot="beforeFilter" style="display: inline-block; margin-right: 5px;">

                            </div>

                            <div slot="afterFilter" class="ml-2">

                            </div>

                            <template slot="seller_name" slot-scope="props">
                                <router-link :to="`/agents/view/${props.row.seller_id}`">{{ props.row.seller_name }}</router-link>
                            </template>

                            <template slot="city" slot-scope="props">
                                {{ props.row.city }} / {{ props.row.state_name }}
                            </template>

                            <template slot="email" slot-scope="props">
                                <a :href="`mailto:${props.row.email}`" v-if="props.row.email">{{ props.row.email }}</a>
                            </template>

                            <template slot="writing_nbr" slot-scope="props">
                                <router-link :to="`/contracts/view/${props.row.id}`" title="View Contract">{{ props.row.writing_nbr ? props.row.writing_nbr : 'View' }}</router-link>
                            </template>

                            <template slot="product_level_name" slot-scope="props">
                                <span v-if="props.row.product_level_name">
                                    {{ props.row.product_level_name }}
                                </span>
                                <span v-else-if="props.row.commission">
                                    <i class="fa fa-xs fa-flag text-warning text-muted" title="This is the Plain Text Commission Value from Legacy AMOS v1"></i> - {{ props.row.commission }}
                                </span>
                            </template>

                            <template slot="id" slot-scope="props">
                                <router-link :to="`/contracts/edit/${props.row.id}`" class="btn btn-xs btn-default"><i class="fa fa-sm fa-edit"></i></router-link>
                                <router-link :to="`/contracts/delete/${props.row.id}`" class="btn btn-xs btn-default"><i class="fa fa-sm fa-trash text-danger"></i></router-link>
                            </template>

                            <template slot="contract_status_id" slot-scope="props">
                                <label :class="'badge badge-'+props.row.contract_status_color">{{ props.row.contract_status_name }}</label>
                            </template>
                        </v-server-table>
                    </div>
                </div>

                <div class="card" v-show="curTab === 'history'">
                    <div class="card-body">
                        <history-list :record_id="product.id" record_type="Product" @historyCount="(count) => { this.historyCount = count; }" />
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    export default {
        data() {
            return {
                loading: true,
                id: this.$route.params.id,
                curTab: 'sold_by',
                agentCount: null,
                historyCount: null,
                product: {},
                columns: [
                    'id',
                    'seller_name',
                    'city',
                    'email',
                    'writing_nbr',
                    'product_level_name',
                    'contract_status_id',
                ],
                options: {
                    headings: {
                        seller_name: 'Agent',
                        city: 'City/State',
                        id: 'Actions',
                        writing_nbr: 'Writing#',
                        contract_status_id: 'Status',
                        product_level_name: 'Level',
                    },
                    texts:{
                        filterPlaceholder:"Search Agents",
                    },
                    orderBy: {
                        ascending: true,
                        column: 'seller_agent.last_name',
                    },
                    responseAdapter: (response) => {
                        //Emit the count
                        this.agentCount = response.data.count;

                        //Send it
                        return response.data;
                    },
                    uniqueKey: 'id',
                },
                files: [],
            }
        },

        computed: {
            canEdit(){
                //Send it
                return this.$root.hasPermission('products.manage');
            }
        },

        methods: {

            getProduct(){
                //Send it
                this.$http.get('/products/view/' + this.id)
                    .then(response => {
                        //Set the product
                        this.product = response.data.product;
                    })
                    .catch(error => {
                        console.log(error);
                    })
                    .then(() => {
                        //Reset
                        this.loading = false;
                    });
            },
        },

        mounted() {
            //get the product?
            this.getProduct();
        }
    }
</script>
